import * as React from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/ui/components/ui/button"
import { cn } from "@/lib/utils"

export interface ErrorStateProps {
  /**
   * Custom error message (overrides default)
   * Default: "An error occurred while loading. Please retry."
   */
  message?: string

  /**
   * Retry action callback
   * If not provided, retry button will not be shown
   */
  onRetry?: () => void

  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * Error State Component
 *
 * Displays an error state for data loading failures (e.g., 500 errors, network issues).
 * NOT for form validation errors - use field-level validation feedback instead.
 *
 * Design specs:
 * - Gap: 2rem (32px) between elements (same as TrophyRoomEmptyState)
 * - Text: 0.9375rem (15px), gray-700
 * - Abstract geometric illustration in gray tones
 * - Retry button: 2.5rem (40px) height, primary-dark variant
 * - Layout: Centered with vertical flex
 *
 * @example Default error with retry
 * ```tsx
 * <ErrorState onRetry={actions.refreshData} />
 * ```
 *
 * @example Custom message
 * ```tsx
 * <ErrorState
 *   message="An error occurred while loading the items. Please retry."
 *   onRetry={actions.refreshItems}
 * />
 * ```
 *
 * @example Informational error (no retry)
 * ```tsx
 * <ErrorState message="Unable to connect to server." />
 * ```
 */
export const ErrorState = React.forwardRef<HTMLDivElement, ErrorStateProps>(
  ({ message, onRetry, className }, ref) => {
    const { t } = useTranslation()

    const errorMessage =
      message ||
      t(
        "common.errors.loadingFailed",
        "An error occurred while loading. Please retry."
      )

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-[2rem] items-center text-center mt-8",
          className
        )}
      >
        {/* Abstract Error Illustration */}
        <svg
          width="120"
          height="120"
          viewBox="0 0 120 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* Disconnected geometric nodes representing error state */}
          {/* Left node - broken connection */}
          <circle cx="30" cy="60" r="12" fill="#BFBFBF" opacity="0.5" />
          <circle cx="30" cy="60" r="6" fill="#D9D9D9" />

          {/* Right node - broken connection */}
          <circle cx="90" cy="60" r="12" fill="#BFBFBF" opacity="0.5" />
          <circle cx="90" cy="60" r="6" fill="#D9D9D9" />

          {/* Broken connection line (dashed) */}
          <line
            x1="42"
            y1="60"
            x2="78"
            y2="60"
            stroke="#BFBFBF"
            strokeWidth="2"
            strokeDasharray="4 4"
            opacity="0.6"
          />

          {/* Error indicator - broken connection symbol */}
          <path
            d="M 55 55 L 65 65 M 65 55 L 55 65"
            stroke="#808080"
            strokeWidth="2.5"
            strokeLinecap="round"
            opacity="0.7"
          />

          {/* Top node - part of broken network */}
          <circle cx="60" cy="25" r="8" fill="#D9D9D9" opacity="0.4" />

          {/* Bottom node - part of broken network */}
          <circle cx="60" cy="95" r="8" fill="#D9D9D9" opacity="0.4" />

          {/* Broken vertical connections */}
          <line
            x1="60"
            y1="33"
            x2="60"
            y2="50"
            stroke="#BFBFBF"
            strokeWidth="1.5"
            strokeDasharray="3 3"
            opacity="0.4"
          />
          <line
            x1="60"
            y1="70"
            x2="60"
            y2="87"
            stroke="#BFBFBF"
            strokeWidth="1.5"
            strokeDasharray="3 3"
            opacity="0.4"
          />
        </svg>

        {/* Error Message */}
        <p className="font-montserrat font-normal text-[0.9375rem] text-gray-700 leading-[1.25rem]">
          {errorMessage}
        </p>

        {/* Retry Button (conditional) */}
        {onRetry && (
          <Button
            variant="primary-dark"
            size="primary-dark"
            onClick={onRetry}
            className="w-full h-[2.5rem]"
          >
            {t("common.retry", "Retry")}
          </Button>
        )}
      </div>
    )
  }
)

ErrorState.displayName = "ErrorState"
